import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, CellarTobacco } from '../lib/supabase';
import BottomNav from '../components/BottomNav';
import { Notebook, Pencil, Plus, ArrowLeft } from 'lucide-react';
import SessionCard from '../components/SessionCard';

type Session = {
  id: string;
  user_id: string;
  cellar_tobacco_id: string;
  rating_small?: number | null;
  rating_10?: number | null;
  notes?: string | null;
  pipe_manual?: string | null;
  lighter_manual?: string | null;
  drink_pairing?: string | null;
  photo_url?: string | null;
  created_at: string;
};

export default function Sessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCellarId, setFilterCellarId] = useState<string>('');
  const [cellar, setCellar] = useState<CellarTobacco[]>([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('sessions_view_mode') : null;
    return (saved === 'grid' || saved === 'list') ? (saved as any) : 'list';
  });

  useEffect(() => {
    localStorage.setItem('sessions_view_mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    loadCellar();
    loadSessions();
  }, []);

  async function loadCellar() {
    const { data } = await supabase.from('cellar_tobacco').select('*').order('created_at', { ascending: false });
    if (data) setCellar(data);
  }

  async function loadSessions() {
    setLoading(true);
    const { data } = await supabase
      .from('sessions')
      .select('*, cellar:cellar_tobacco(*)')
      .order('created_at', { ascending: false });
    if (data) setSessions(data as any);
    setLoading(false);
  }

  const filtered = filterCellarId ? sessions.filter(s => s.cellar_tobacco_id === filterCellarId) : sessions;

  useEffect(() => {
    if (!showFilterDropdown) return;
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (!filterDropdownRef.current) return;
      if (!filterDropdownRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const selectedFilterLabel = filterCellarId
    ? (() => {
        const match = cellar.find((c) => c.id === filterCellarId);
        return match ? `${match.brand} - ${match.blend_name}` : 'All Items';
      })()
    : 'All Items';

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}>
            <ArrowLeft size={24} />
          </button>
          <Notebook size={24} color="var(--color-accent)" />
          <h1 style={{ fontSize: '1.5rem' }}>Sessions</h1>
        </div>
        <Link to="/sessions/new" style={{ textDecoration: 'none' }}>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--color-secondary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            <Plus size={16} />
            Add Session
          </button>
        </Link>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        <div className="card" style={{
          marginBottom: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '0.75rem',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: '1 1 260px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem' }}>Filter by Cellar Item:</label>
            <div ref={filterDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowFilterDropdown((prev) => !prev)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  backgroundColor: showFilterDropdown ? 'rgba(196, 255, 0, 0.12)' : 'var(--color-card)',
                  border: `1px solid ${showFilterDropdown ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  borderRadius: '6px',
                  color: selectedFilterLabel ? 'var(--color-text)' : '#666',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFilterLabel}
                </span>
                <span style={{ color: 'var(--color-accent)', transform: showFilterDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                  ▾
                </span>
              </button>
            {showFilterDropdown && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 'calc(100% + 4px)',
                  zIndex: 20,
                  backgroundColor: '#1a1a1a',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  maxHeight: '280px',
                  overflowY: 'auto',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
                }}
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setFilterCellarId('');
                    setShowFilterDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.75rem',
                    border: 'none',
                    background: filterCellarId === '' ? 'rgba(196, 255, 0, 0.12)' : 'transparent',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: filterCellarId === '' ? 600 : 500
                  }}
                >
                  All Items
                </button>
                {cellar.map((c) => {
                  const label = `${c.brand} - ${c.blend_name}`;
                  const isSelected = c.id === filterCellarId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setFilterCellarId(c.id);
                        setShowFilterDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '0.75rem',
                        border: 'none',
                        background: isSelected ? 'rgba(196, 255, 0, 0.12)' : 'transparent',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: isSelected ? 600 : 500
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '0 1 auto' }}>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              title="Toggle view"
              style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid var(--color-border)', backgroundColor: viewMode === 'grid' ? 'var(--color-accent)' : 'var(--color-card)', color: viewMode === 'grid' ? '#0b0b0b' : '#ccc', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              View
            </button>
          </div>
        </div>

        {loading ? (
          <div className="card" style={{ padding: '1rem' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: '1rem' }}>No sessions yet.</div>
        ) : (
          viewMode === 'list' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {filtered.map(s => (
                <SessionCard key={s.id} session={s} onDelete={loadSessions} onEdit={() => navigate(`/sessions/edit/${s.id}`)} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }}>
              {filtered.map(s => (
                <div key={s.id}>
                  <SessionCard session={s} onDelete={loadSessions} onEdit={() => navigate(`/sessions/edit/${s.id}`)} />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <BottomNav />
    </div>
  );
}


